import { useMemo, useState } from 'react'
import type { Party, Candidate } from '@/types'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { PoliticalMap2D, PoliticalMapLegend, type MapEntity } from '@/components/ideology/PoliticalMap2D'
import { CandidateMapSelector, CandidateMapSelectorCompact } from '@/components/ideology/CandidateMapSelector'
import { PoliticalLabel } from '@/components/ideology/PoliticalLabel'
import { calculateDistance, distanceToSimilarity } from '@/lib/ideology'

// ============================================================================
// Types
// ============================================================================

interface PartyIdeologyMapProps {
  party: Party
  candidates: Candidate[]
  partyLeaderId?: string
  isOwner?: boolean
  className?: string
}

// ============================================================================
// Component
// ============================================================================

export function PartyIdeologyMap({
  party,
  candidates,
  partyLeaderId,
  isOwner,
  className,
}: PartyIdeologyMapProps) {
  const [selectedCandidateIds, setSelectedCandidateIds] = useState<string[]>(() => {
    // Default to showing party leader if exists, otherwise all
    if (partyLeaderId) {
      return [partyLeaderId]
    }
    return candidates.map(c => c.id)
  })
  const [highlightedId, setHighlightedId] = useState<string | null>(null)

  // Convert party and candidates to map entities
  const mapEntities: MapEntity[] = useMemo(() => {
    const entities: MapEntity[] = [
      // Party is always shown
      {
        id: party.id,
        name: party.name,
        ideology: party.ideology_vector,
        color: party.color,
        type: 'party' as const,
      }
    ]

    // Add selected candidates
    const selectedCandidates = candidates.filter(c => selectedCandidateIds.includes(c.id))
    selectedCandidates.forEach(candidate => {
      entities.push({
        id: candidate.id,
        name: candidate.name,
        ideology: candidate.ideology_vector,
        color: adjustColorOpacity(party.color, 0.6), // Lighter shade for candidates
        type: 'candidate' as const,
      })
    })

    return entities
  }, [party, candidates, selectedCandidateIds])

  // Identify candidates that are far from party ideology
  const ideologicalOutliers = useMemo(() => {
    return candidates.filter(c => {
      const distance = calculateDistance(c.ideology_vector, party.ideology_vector)
      const similarity = distanceToSimilarity(distance)
      return similarity < 60 // Less than 60% similarity = potential loyalty risk
    })
  }, [candidates, party.ideology_vector])

  const handleEntityHover = (id: string | null) => {
    setHighlightedId(id)
  }

  if (candidates.length === 0) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            Party Ideology Map
            <PoliticalLabel ideology={party.ideology_vector} size="sm" />
          </CardTitle>
          <CardDescription>
            Recruit candidates to see their ideological positions relative to the party.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center py-4">
            <PoliticalMap2D
              entities={[{
                id: party.id,
                name: party.name,
                ideology: party.ideology_vector,
                color: party.color,
                type: 'party',
              }]}
              width={300}
              height={300}
              showGridLabels={true}
              showSubLabels={true}
              showAxisLabels={true}
            />
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          Party Ideology Map
          <PoliticalLabel ideology={party.ideology_vector} size="sm" />
        </CardTitle>
        <CardDescription>
          View where your candidates stand relative to the party's ideology.
          {ideologicalOutliers.length > 0 && isOwner && (
            <span className="text-yellow-600 dark:text-yellow-400 ml-1">
              {ideologicalOutliers.length} candidate{ideologicalOutliers.length > 1 ? 's' : ''} may have loyalty concerns.
            </span>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Candidate Selector */}
        <CandidateMapSelectorCompact
          candidates={candidates}
          selectedIds={selectedCandidateIds}
          onSelectionChange={setSelectedCandidateIds}
          partyLeaderId={partyLeaderId}
        />

        {/* Map and Legend */}
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Map */}
          <div className="flex-shrink-0 flex justify-center">
            <PoliticalMap2D
              entities={mapEntities}
              width={320}
              height={320}
              showGridLabels={true}
              showSubLabels={true}
              showAxisLabels={true}
              onEntityHover={handleEntityHover}
              highlightedId={highlightedId || party.id}
            />
          </div>

          {/* Legend and Info */}
          <div className="flex-1 min-w-0 space-y-4">
            {/* Entities legend */}
            <div>
              <h4 className="text-sm font-medium text-muted-foreground mb-2">
                On Map
              </h4>
              <PoliticalMapLegend
                entities={mapEntities}
                onEntityHover={handleEntityHover}
                highlightedId={highlightedId || party.id}
              />
            </div>

            {/* Outliers warning */}
            {ideologicalOutliers.length > 0 && isOwner && (
              <div className="border-t pt-3">
                <h4 className="text-sm font-medium text-yellow-600 dark:text-yellow-400 mb-2">
                  Loyalty Concerns
                </h4>
                <p className="text-xs text-muted-foreground mb-2">
                  These candidates have ideologies significantly different from the party:
                </p>
                <ul className="text-xs space-y-1">
                  {ideologicalOutliers.map(c => {
                    const similarity = distanceToSimilarity(
                      calculateDistance(c.ideology_vector, party.ideology_vector)
                    )
                    return (
                      <li key={c.id} className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-yellow-500" />
                        <span>{c.name}</span>
                        <span className="text-muted-foreground">
                          ({Math.round(similarity)}% aligned)
                        </span>
                      </li>
                    )
                  })}
                </ul>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Adjusts a hex color's opacity by creating a lighter version.
 */
function adjustColorOpacity(hexColor: string, factor: number): string {
  // For simplicity, just return the original color with adjusted brightness
  // A full implementation would convert to HSL and adjust
  const hex = hexColor.replace('#', '')
  const r = parseInt(hex.substring(0, 2), 16)
  const g = parseInt(hex.substring(2, 4), 16)
  const b = parseInt(hex.substring(4, 6), 16)

  // Blend with white based on factor (1 = original, 0 = white)
  const blend = (color: number) => Math.round(color * factor + 255 * (1 - factor))

  const newR = blend(r).toString(16).padStart(2, '0')
  const newG = blend(g).toString(16).padStart(2, '0')
  const newB = blend(b).toString(16).padStart(2, '0')

  return `#${newR}${newG}${newB}`
}

// ============================================================================
// Expanded Full Selector Version
// ============================================================================

interface PartyIdeologyMapExpandedProps {
  party: Party
  candidates: Candidate[]
  partyLeaderId?: string
  className?: string
}

export function PartyIdeologyMapExpanded({
  party,
  candidates,
  partyLeaderId,
  className,
}: PartyIdeologyMapExpandedProps) {
  const [selectedCandidateIds, setSelectedCandidateIds] = useState<string[]>(
    candidates.map(c => c.id)
  )
  const [highlightedId, setHighlightedId] = useState<string | null>(null)

  // Convert party and candidates to map entities
  const mapEntities: MapEntity[] = useMemo(() => {
    const entities: MapEntity[] = [{
      id: party.id,
      name: party.name,
      ideology: party.ideology_vector,
      color: party.color,
      type: 'party' as const,
    }]

    candidates
      .filter(c => selectedCandidateIds.includes(c.id))
      .forEach(candidate => {
        entities.push({
          id: candidate.id,
          name: candidate.name,
          ideology: candidate.ideology_vector,
          color: adjustColorOpacity(party.color, 0.7),
          type: 'candidate' as const,
        })
      })

    return entities
  }, [party, candidates, selectedCandidateIds])

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>Party Ideology Map</CardTitle>
        <CardDescription>
          Visualize the ideological spread of your candidates
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Map */}
          <div className="lg:col-span-2 flex justify-center">
            <PoliticalMap2D
              entities={mapEntities}
              width={400}
              height={400}
              showGridLabels={true}
              showSubLabels={true}
              showAxisLabels={true}
              onEntityHover={setHighlightedId}
              highlightedId={highlightedId || party.id}
            />
          </div>

          {/* Full candidate selector */}
          <div>
            <h4 className="text-sm font-medium mb-3">Select Candidates</h4>
            <CandidateMapSelector
              candidates={candidates}
              selectedIds={selectedCandidateIds}
              onSelectionChange={setSelectedCandidateIds}
              partyLeaderId={partyLeaderId}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
